<script setup lang="ts" generic="T">
import { computed } from 'vue';
import EntrPanelScroller from './EntrPanelScroller.vue';

const props = withDefaults(
  defineProps<{
    items: readonly T[];
    columns: number;
    rows: number;
    itemKey: (item: T, index: number) => string | number;
    ariaLabel: string;
    testId?: string;
    gap?: string;
    panelGap?: string;
  }>(),
  {
    gap: '32px',
    panelGap: '128px',
  }
);

defineSlots<{
  item(props: { item: T; index: number }): unknown;
}>();

const itemsPerPanel = computed(() => props.columns * props.rows);
const panels = computed(() => {
  const result: { items: readonly T[]; startIndex: number }[] = [];

  for (
    let index = 0;
    index < props.items.length;
    index += itemsPerPanel.value
  ) {
    result.push({
      items: props.items.slice(index, index + itemsPerPanel.value),
      startIndex: index,
    });
  }

  return result;
});
</script>

<template>
  <EntrPanelScroller
    class="entr-grid-panel"
    :style="{ '--grid-gap': gap, '--panel-gap': panelGap }"
    :panel-count="panels.length"
    :ariaLabel="ariaLabel"
    :test-id="testId"
  >
    <template #default="{ panelClass }">
      <TransitionGroup
        tag="div"
        name="grid-item"
        v-for="(panel, panelIndex) in panels"
        :key="panelIndex"
        :class="[panelClass, 'entr-grid-panel__grid']"
        :style="{ '--grid-columns': columns }"
      >
        <template
          v-for="(item, itemIndex) in panel.items"
          :key="itemKey(item, panel.startIndex + itemIndex)"
        >
          <slot
            name="item"
            :item="item"
            :index="panel.startIndex + itemIndex"
          />
        </template>
      </TransitionGroup>
    </template>
  </EntrPanelScroller>
</template>

<style scoped>
.entr-grid-panel {
  height: 100%;
}

.entr-grid-panel__grid {
  flex: 0 0 100%;
  display: grid;
  grid-template-columns: repeat(var(--grid-columns), 1fr);
  gap: var(--grid-gap);
  align-content: start;
  padding-bottom: 20px;
  scroll-snap-align: start;
}

.grid-item-enter-active {
  transition:
    opacity 240ms ease-out,
    transform 240ms ease-out;
}

.grid-item-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
</style>
